import { useCallback, useEffect, useRef, useState } from 'react';
import i18n from 'i18n/config';
import RecordRTC from 'recordrtc';

// The test recording succeeds as soon as the encoder produces data. Chrome emits nothing
// until the camera's first frame, which can take seconds on some devices, so only give up
// once the ceiling is reached.
const RECORDING_TEST_SLICE_MS = 250;
const RECORDING_TEST_TIMEOUT_MS = 5000;

// The compatibility test must validate the configuration the recorder actually uses: the
// camera's native resolution is slower to start, slower to produce a first keyframe, and
// rejects devices that are fine at the resolution we record at. `frameRate` sits outside
// `video` so getUserMedia ignores it - left as-is to keep the real acquisition unchanged.
const CAMERA_CONSTRAINTS = {
  video: {
    width: { max: 640, ideal: 640, min: 320 },
    height: { max: 480, ideal: 480, min: 240 }
  },
  frameRate: 15,
  audio: true
};

export const useMediaStream = () => {
  const [stream, setStream] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const streamRef = useRef(null);

  //Function to check if RecordRTC video will be supported
  const isRecordingSupported = useCallback(async () => {
    try {
      // Check for basic WebRTC support
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        throw new Error('WEBRTC_NOT_SUPPORTED');
      }

      // Check for canvas support (required by RecordRTC)
      const canvas = document.createElement('canvas');
      if (!canvas.getContext) {
        throw new Error('CANVAS_NOT_SUPPORTED');
      }

      // Test actual media stream access
      const stream = await navigator.mediaDevices.getUserMedia(
        CAMERA_CONSTRAINTS
      );

      // Check if we actually got video and audio tracks
      const videoTracks = stream.getVideoTracks();
      const audioTracks = stream.getAudioTracks();

      if (videoTracks.length === 0 || audioTracks.length === 0) {
        stream.getTracks().forEach(t => t.stop());
        throw new Error('NO_VIDEO_OR_AUDIO_TRACKS');
      }

      return new Promise((resolve, reject) => {
        const stopStream = () => stream.getTracks().forEach(t => t.stop());
        let recorder = null;
        let settled = false;
        let timeoutId = null;

        const finish = isSupported => {
          if (settled) return;
          settled = true;
          clearTimeout(timeoutId);

          try {
            recorder.stopRecording(stopStream);
          } catch (err) {
            stopStream();
          }

          if (isSupported) {
            resolve(true);
          } else {
            reject(new Error('RECORDING_TEST_FAILED'));
          }
        };

        recorder = new RecordRTC(stream, {
          type: 'video',
          mimeType: 'video/webm;codecs=vp8,opus',
          disableLogs: true,
          timeSlice: RECORDING_TEST_SLICE_MS,
          ondataavailable: blob => {
            if (blob && blob.size > 0) {
              finish(true);
            }
          }
        });

        if (!recorder) {
          stopStream();
          reject(new Error('RECORDER_CREATION_FAILED'));
          return;
        }

        timeoutId = setTimeout(() => finish(false), RECORDING_TEST_TIMEOUT_MS);

        try {
          recorder.startRecording();
        } catch (err) {
          console.error('Error starting the test video record');
          clearTimeout(timeoutId);
          stopStream();
          reject(new Error('RECORDING_START_FAILED'));
        }
      });
    } catch (err) {
      console.error('Error checking recording support:', err);
      throw err;
    }
  }, []);

  const initializeMedia = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      // Check if RecordRTC is supported
      if (typeof RecordRTC === 'undefined') {
        throw new Error('RECORDRTC_NOT_AVAILABLE');
      }

      await isRecordingSupported();

      // Attempt to get the media stream
      const mediaStream = await navigator.mediaDevices.getUserMedia(
        CAMERA_CONSTRAINTS
      );
      streamRef.current = mediaStream;
      setStream(mediaStream);
      setError(null);
      setLoading(false);
    } catch (err) {
      setLoading(false);

      if (err.message === 'RECORDRTC_NOT_AVAILABLE') {
        setError('Video Recording Error: Video recording is not available.');
        console.error(
          'Video Recording Error: RecordRTC library not found or undefined:',
          err
        );
      } else if (err.message === 'WEBRTC_NOT_SUPPORTED') {
        setError(
          'Video Recording Error: Your browser does not support video recording technology.'
        );
        console.error('Video Recording Error: WebRTC APIs not supported:', err);
      } else if (err.message === 'CANVAS_NOT_SUPPORTED') {
        setError(
          'Video Recording Error: Your browser does not support video recording features.'
        );
        console.error('Video Recording Error: Canvas API not supported:', err);
      } else if (err.message === 'NO_VIDEO_OR_AUDIO_TRACKS') {
        setError('Video Recording Error: No video or audio tracks detected.');
        console.error(
          'Video Recording Error: No video or audio tracks in media stream:',
          err
        );
      } else if (err.message === 'RECORDER_CREATION_FAILED') {
        setError('Video Recording Error: Failed to create video recorder.');
        console.error(
          'Video Recording Error: RecordRTC recorder creation failed:',
          err
        );
      } else if (err.message === 'RECORDING_START_FAILED') {
        setError('Video Recording Error: Failed to start test recording.');
        console.error(
          'Video Recording Error: Test recording start failed:',
          err
        );
      } else if (err.message === 'RECORDING_STOP_FAILED') {
        setError('Video Recording Error: Failed to stop test recording.');
        console.error(
          'Video Recording Error: Test recording stop failed:',
          err
        );
      } else if (err.message === 'RECORDING_TEST_FAILED') {
        setError('Video Recording Error: Test recording failed.');
        console.error(
          'Video Recording Error: Test recording validation failed - invalid blob or size:',
          err
        );
      } else if (
        err.name === 'NotFoundError' ||
        err.name === 'DevicesNotFoundError' ||
        err.message === 'DevicesNotFoundError'
      ) {
        setError(i18n.t('troubleshootingPanel.noCameraOrMic'));
        console.error('Video Recording Error: Media devices not found:', err);
      } else if (
        err.name === 'NotAllowedError' ||
        err.name === 'PermissionDeniedError' ||
        err.message === 'NotAllowedError'
      ) {
        setError(i18n.t('troubleshootingPanel.permissionDenied'));
        console.error('Video Recording Error: Media permission denied:', err);
      } else {
        setError(`Unable to access camera or microphone: ${err.message}`);
        console.error(
          'Video Recording Error: Unknown media access error:',
          err
        );
      }
      setStream(null);
    }
  }, [setLoading, setError, setStream, streamRef, isRecordingSupported]);

  useEffect(() => {
    initializeMedia();

    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { stream, error, loading, initializeMedia };
};

export const useAudioAnalyser = stream => {
  const [micLevel, setMicLevel] = useState(0);
  const analyserRef = useRef(null);
  const rafRef = useRef(null);

  useEffect(() => {
    if (!stream) return;

    const audioContext = new (window.AudioContext ||
      window.webkitAudioContext)();
    const analyser = audioContext.createAnalyser();
    const microphone = audioContext.createMediaStreamSource(stream);
    microphone.connect(analyser);
    analyser.fftSize = 256;
    analyserRef.current = analyser;

    const updateMicLevel = () => {
      const dataArray = new Uint8Array(analyser.frequencyBinCount);
      analyser.getByteFrequencyData(dataArray);
      const average =
        dataArray.reduce((acc, val) => acc + val, 0) / dataArray.length;
      const newMicLevel = average / 128; // Normalize to 0-1 range

      // Only update state if the change is significant
      setMicLevel(newMicLevel);

      rafRef.current = requestAnimationFrame(updateMicLevel);
    };

    updateMicLevel();

    return () => {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }
      if (audioContext.state !== 'closed') {
        audioContext.close();
      }
    };
  }, [stream]);

  return micLevel;
};
